import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Gauge, Users } from "lucide-react";

export function usePlanUsage() {
  const [u, setU] = useState(null);
  useEffect(() => {
    api.get("/admin/plan-usage").then((r) => setU(r.data)).catch(() => {});
  }, []);
  return u;
}

function Meter({ used, limit, warn, atLimit }) {
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const bar = atLimit ? "bg-red-500" : warn ? "bg-amber-400" : "bg-sky-500";
  return (
    <div className="mt-2 h-2 rounded-full bg-gray-200 overflow-hidden">
      <div className={`h-full rounded-full transition-all ${bar}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function PlanUsageCard({ detailed = false }) {
  const u = usePlanUsage();
  if (!u) return null;
  const { plan } = u;
  return (
    <div className="rcw-card p-4 sm:p-5 mt-6" data-testid="plan-usage-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Gauge className="h-4 w-4 text-sky-500" />
          <div className="text-xs uppercase tracking-widest font-bold text-gray-500">
            {plan.name} plan usage
          </div>
        </div>
        {detailed && <span className="text-xs text-gray-400">{plan.price}</span>}
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mt-3">
        <div data-testid="plan-captures-meter">
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-gray-700">
              <span className="font-bold text-gray-900">{u.captures_used}</span> of {plan.captures_limit} captures used this month
            </span>
            {u.captures_warn && (
              <span className="text-xs font-semibold text-amber-600" data-testid="plan-captures-warning">
                Only {u.captures_remaining} left
              </span>
            )}
          </div>
          <Meter used={u.captures_used} limit={plan.captures_limit} warn={u.captures_warn} atLimit={u.captures_at_limit} />
          {u.captures_at_limit && (
            <div className="mt-2 text-xs font-medium text-red-600" data-testid="plan-captures-limit-note">
              You have reached the limit of your {plan.name} plan this month. New captures open next month.
            </div>
          )}
        </div>
        <div data-testid="plan-users-meter">
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-gray-700 inline-flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5 text-gray-400" />
              <span className="font-bold text-gray-900">{u.users_used}</span>&nbsp;of {plan.users_limit} users
            </span>
          </div>
          <Meter used={u.users_used} limit={plan.users_limit} warn={false} atLimit={u.users_at_limit} />
          {u.users_at_limit && (
            <div className="mt-2 text-xs text-gray-500">All user seats are in use.</div>
          )}
        </div>
      </div>
      {detailed && (
        <p className="text-xs text-gray-400 mt-4">
          A capture is any new project or case study submitted (including uploaded imports). The allowance
          resets on the 1st of each month. Need more? Growth gives up to 75 captures and 15 users.
        </p>
      )}
    </div>
  );
}
